import React from "react";
import { TemplateProps, createTemplateUtils } from "../BaseTemplate";
import BasicInfoSection from "../../offer-wizard/BasicInfoSection";
import PublicPlanSummary from "../PublicPlanSummary";
import PublicFinancialSummary from "../PublicFinancialSummary";
import { analyticsService } from "../../../services/analyticsService";

const CompactTemplate: React.FC<TemplateProps> = ({
  offer,
  brandKit,
  termsAndConditions,
  editMode = false,
  onColorChange,
  showColorPicker,
  setShowColorPicker,
  onHeroUpload,
  onHeroRemove,
  uploadingHero = false,
  onTermsToggle,
  onTermsContentChange,
}) => {
  const utils = createTemplateUtils(brandKit);

  const handleClick = (e: React.MouseEvent) => {
    if (!editMode && analyticsService.isSessionActive()) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      analyticsService.trackClick(
        (e.target as HTMLElement).tagName.toLowerCase(),
        x,
        y,
        "offer-page"
      );
    }
  };

  return (
    <div
      className="font-mono text-sm"
      onClick={handleClick}
      style={
        {
          "--primary-color": utils.colors.primary || "#374151",
          "--secondary-color": utils.colors.secondary || "#6B7280",
          "--accent-color": utils.colors.accent || "#059669",
        } as React.CSSProperties
      }
    >
      <div className="max-w-4xl mx-auto bg-white p-6">
        {/* Compact Header - Table Style */}
        <table className="w-full border-2 border-gray-800 mb-6">
          <tr>
            <td
              className="border border-gray-800 p-3 bg-gray-800 text-white font-bold text-center"
              colSpan={4}
            >
              OFERTA HOTELOWA
            </td>
          </tr>
          <tr>
            <td className="border border-gray-800 p-2 font-semibold bg-gray-100">
              Tytuł:
            </td>
            <td className="border border-gray-800 p-2">{offer.title}</td>
            <td className="border border-gray-800 p-2 font-semibold bg-gray-100">
              Nr oferty:
            </td>
            <td className="border border-gray-800 p-2">
              #{offer.id.slice(-8).toUpperCase()}
            </td>
          </tr>
          <tr>
            <td className="border border-gray-800 p-2 font-semibold bg-gray-100">
              Klient:
            </td>
            <td className="border border-gray-800 p-2">{offer.clientName}</td>
            <td className="border border-gray-800 p-2 font-semibold bg-gray-100">
              Data:
            </td>
            <td className="border border-gray-800 p-2">
              {utils.formatDate(new Date().toISOString())}
            </td>
          </tr>
          <tr>
            <td className="border border-gray-800 p-2 font-semibold bg-gray-100">
              Hotel:
            </td>
            <td className="border border-gray-800 p-2" colSpan={3}>
              {offer.hotel.name}
            </td>
          </tr>
        </table>

        {/* Key Visual - Compact */}
        {(utils.keyVisualUrl || editMode) && (
          <div className="relative group mb-6">
            {utils.keyVisualUrl ? (
              <div className="relative">
                <img
                  src={utils.keyVisualUrl}
                  alt="Key Visual"
                  className="w-full h-32 object-cover border border-gray-300"
                />
                {editMode && (
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-black group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
                    <div className="hidden group-hover:flex space-x-2">
                      <button
                        onClick={() => {
                          const input = document.createElement("input");
                          input.type = "file";
                          input.accept = "image/jpeg,image/png,image/webp";
                          input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement)
                              .files?.[0];
                            if (file) onHeroUpload?.(file);
                          };
                          input.click();
                        }}
                        className="px-3 py-1 bg-white text-gray-800 text-xs font-medium hover:bg-gray-100"
                        disabled={uploadingHero}
                      >
                        {uploadingHero ? "..." : "Podmień"}
                      </button>
                      <button
                        onClick={onHeroRemove}
                        className="px-3 py-1 bg-red-600 text-white text-xs font-medium hover:bg-red-700"
                      >
                        Usuń
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : editMode ? (
              <div className="h-24 border border-dashed border-gray-400 flex items-center justify-center">
                <button
                  onClick={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "image/jpeg,image/png,image/webp";
                    input.onchange = (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) onHeroUpload?.(file);
                    };
                    input.click();
                  }}
                  className="px-4 py-2 text-xs border border-gray-400 hover:bg-gray-50"
                  disabled={uploadingHero}
                >
                  {uploadingHero ? "..." : "Dodaj obrazek"}
                </button>
              </div>
            ) : null}
          </div>
        )}

        {/* Compact Content Sections */}
        <div className="space-y-4 mb-6">
          <div className="border border-gray-400 p-4">
            <BasicInfoSection
              data={{
                title: offer.title,
                clientName: offer.clientName,
                clientEmail: offer.clientEmail,
                clientPhone: offer.clientPhone,
                description: offer.description || "",
                checkInDate: offer.checkInDate,
                checkOutDate: offer.checkOutDate,
                numberOfGuests: offer.numberOfGuests,
                numberOfRooms: offer.numberOfRooms,
                offerDays: offer.offerDays,
                currentDay: 1,
                netValue: offer.netValue,
              }}
              mode="public"
            />
          </div>
          <div className="border border-gray-400 p-4">
            <PublicPlanSummary offer={offer} />
          </div>
          <div className="border border-gray-400 p-4">
            {offer.financialSummary && (
              <PublicFinancialSummary
                financialSummary={offer.financialSummary}
              />
            )}
          </div>
        </div>

        {/* Terms - Compact */}
        {termsAndConditions?.enabled ? (
          <div className="relative group border border-gray-400 p-3 mb-4 bg-gray-50">
            {editMode && (
              <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100">
                <button
                  onClick={() => onTermsToggle?.(false)}
                  className="px-2 py-1 bg-red-600 text-white text-xs hover:bg-red-700"
                >
                  X
                </button>
              </div>
            )}
            <h4 className="font-bold mb-2 text-xs uppercase">Warunki oferty</h4>
            {editMode ? (
              <textarea
                value={termsAndConditions.content || ""}
                onChange={(e) => onTermsContentChange?.(e.target.value)}
                placeholder="Warunki..."
                className="w-full h-16 p-2 border border-gray-300 text-xs resize-none"
              />
            ) : (
              <div
                className="text-xs text-gray-700"
                dangerouslySetInnerHTML={{
                  __html:
                    termsAndConditions.content?.replace(/\n/g, "<br>") || "",
                }}
              />
            )}
          </div>
        ) : editMode ? (
          <div className="border border-dashed border-gray-400 p-3 text-center mb-4">
            <button
              onClick={() => onTermsToggle?.(true)}
              className="px-3 py-1 text-xs border border-gray-400 hover:bg-gray-50"
            >
              + Warunki
            </button>
          </div>
        ) : null}

        {/* Compact Footer Table */}
        <table className="w-full border border-gray-800 text-xs">
          <tr>
            <td className="border border-gray-800 p-2 bg-gray-100 font-semibold">
              Kontakt:
            </td>
            <td className="border border-gray-800 p-2">
              {offer.hotel.phone && `Tel: ${offer.hotel.phone}`}
              {offer.hotel.phone && offer.hotel.email && " | "}
              {offer.hotel.email && `Email: ${offer.hotel.email}`}
            </td>
          </tr>
          <tr>
            <td className="border border-gray-800 p-2 bg-gray-100 font-semibold">
              Ważność:
            </td>
            <td className="border border-gray-800 p-2">
              Do{" "}
              {utils.formatDate(
                new Date(
                  new Date(offer.publishedAt || offer.createdAt).getTime() +
                    30 * 24 * 60 * 60 * 1000
                ).toISOString()
              )}
            </td>
          </tr>
          <tr>
            <td
              className="border border-gray-800 p-2 bg-gray-100 font-semibold text-center"
              colSpan={2}
            >
              <button
                className="px-4 py-1 text-white font-medium mr-2"
                style={{ backgroundColor: utils.colors.accent || "#059669" }}
              >
                AKCEPTUJĘ
              </button>
              <button className="px-4 py-1 border border-gray-400 text-gray-700 font-medium">
                ZMIANY
              </button>
            </td>
          </tr>
        </table>
      </div>
    </div>
  );
};

export default CompactTemplate;
